import React from "react";

interface IconProps {
  className?: string;
  size?: number;
}

// ─── STAT CARD ICONS ───

export const FlameIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C12 2 6.5 8 6.5 13.5C6.5 17.09 9.41 20 13 20C13 20 10 17.5 10 14.5C10 11.5 12 9 12 9C12 9 14 11.5 14 14.5C14 16.5 13.2 18 12.5 19C15.8 18.5 18 15.8 18 13C18 7.5 12 2 12 2Z" fill="currentColor" opacity="0.25"/>
    <path d="M12 2C12 2 7 7.5 7 13C7 16.87 9.69 19.5 12.5 19.9C11.3 18.8 10.5 16.8 10.5 14.5C10.5 11.2 12.5 8.5 12.5 8.5C12.5 8.5 14.5 11.2 14.5 14.5C14.5 16.2 14 17.6 13.3 18.7C13.5 18.7 13.8 18.7 14 18.7C16.5 18.2 17.5 15.8 17.5 13.5C17.5 8 12 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11 16.5C11 15 12 13.5 12 13.5C12 13.5 13 15 13 16.5C13 17.33 12.55 18 12 18C11.45 18 11 17.33 11 16.5Z" fill="currentColor" opacity="0.6"/>
  </svg>
);

export const TargetIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5" opacity="0.3"/>
    <circle cx="12" cy="12" r="6.5" stroke="currentColor" strokeWidth="1.5" opacity="0.5"/>
    <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="1.5" opacity="0.8"/>
    <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
    <line x1="12" y1="1" x2="12" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
    <line x1="12" y1="20" x2="12" y2="23" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
    <line x1="1" y1="12" x2="4" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
    <line x1="20" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.4"/>
  </svg>
);

export const ClockIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5"/>
    {/* Tick marks */}
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle) => {
      const rad = (angle * Math.PI) / 180;
      const isMajor = angle % 90 === 0;
      const r1 = isMajor ? 7.5 : 8.2;
      const r2 = 9;
      return (
        <line
          key={angle}
          x1={12 + r1 * Math.sin(rad)}
          y1={12 - r1 * Math.cos(rad)}
          x2={12 + r2 * Math.sin(rad)}
          y2={12 - r2 * Math.cos(rad)}
          stroke="currentColor"
          strokeWidth={isMajor ? 1.5 : 0.8}
          strokeLinecap="round"
          opacity={isMajor ? 0.7 : 0.35}
        />
      );
    })}
    {/* Hour hand */}
    <line x1="12" y1="12" x2="12" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    {/* Minute hand */}
    <line x1="12" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.8"/>
    <circle cx="12" cy="12" r="1.2" fill="currentColor"/>
  </svg>
);

export const QuizBookIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Left page */}
    <path d="M2 4.5C2 3.67 2.67 3 3.5 3H11V19H3.5C2.67 19 2 18.33 2 17.5V4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Right page */}
    <path d="M22 4.5C22 3.67 21.33 3 20.5 3H13V19H20.5C21.33 19 22 18.33 22 17.5V4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Spine shadow */}
    <path d="M11 3H13V19H11V3Z" fill="currentColor" opacity="0.15"/>
    {/* Left page lines */}
    <line x1="4.5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="4.5" y1="9.5" x2="9" y2="9.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    <line x1="4.5" y1="12" x2="8" y2="12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
    {/* Right page lines */}
    <line x1="15" y1="7" x2="19.5" y2="7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="15" y1="9.5" x2="19.5" y2="9.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    <line x1="15" y1="12" x2="18.5" y2="12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
    {/* Question mark accent */}
    <path d="M17 14.5C17 14.5 18.2 14 18.2 15.3C18.2 16 17.5 16.3 17.5 17" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
    <circle cx="17.5" cy="18" r="0.5" fill="currentColor" opacity="0.5"/>
  </svg>
);

export const TrophyIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Cup body */}
    <path d="M7 4H17V10C17 13.31 14.76 16 12 16C9.24 16 7 13.31 7 10V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Cup fill */}
    <path d="M8 5H16V10C16 12.76 14.21 15 12 15C9.79 15 8 12.76 8 10V5Z" fill="currentColor" opacity="0.12"/>
    {/* Left handle */}
    <path d="M7 6H5.5C4.67 6 4 6.67 4 7.5V8.5C4 10.16 5.34 11.5 7 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Right handle */}
    <path d="M17 6H18.5C19.33 6 20 6.67 20 7.5V8.5C20 10.16 18.66 11.5 17 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Stem */}
    <line x1="12" y1="16" x2="12" y2="19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Base */}
    <path d="M8.5 19H15.5C15.5 19 16 19 16 19.5C16 20 15.5 20 15.5 20H8.5C8.5 20 8 20 8 19.5C8 19 8.5 19 8.5 19Z" fill="currentColor" opacity="0.5"/>
    {/* Star */}
    <path d="M12 7L12.7 8.8L14.5 8.8L13.1 10L13.5 11.8L12 10.8L10.5 11.8L10.9 10L9.5 8.8L11.3 8.8L12 7Z" fill="currentColor" opacity="0.5"/>
  </svg>
);

// ─── NAVIGATION ICONS ───

export const NewSheetIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Central sparkle */}
    <path d="M12 2L13.5 8.5L20 7L14.5 11L18 17L12 13.5L6 17L9.5 11L4 7L10.5 8.5L12 2Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
    <path d="M12 4L13 8.5L18 7.5L14 10.5L16.5 15.5L12 12.5L7.5 15.5L10 10.5L6 7.5L11 8.5L12 4Z" fill="currentColor" opacity="0.15"/>
    {/* Small sparkle particles */}
    <circle cx="19" cy="3" r="0.8" fill="currentColor" opacity="0.5"/>
    <circle cx="20.5" cy="5" r="0.5" fill="currentColor" opacity="0.35"/>
    <line x1="4" y1="19" x2="5.5" y2="20.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="3" y1="21" x2="4" y2="20" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    {/* Plus accent */}
    <line x1="20" y1="18" x2="20" y2="22" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.6"/>
    <line x1="18" y1="20" x2="22" y2="20" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.6"/>
  </svg>
);

export const FolderIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Folder body */}
    <path d="M2.5 7.5V18.5C2.5 19.6 3.4 20.5 4.5 20.5H19.5C20.6 20.5 21.5 19.6 21.5 18.5V9.5C21.5 8.4 20.6 7.5 19.5 7.5H12L10 4.5H4.5C3.4 4.5 2.5 5.4 2.5 6.5V7.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Folder fill */}
    <path d="M3 8H21V18.5C21 19.33 20.33 20 19.5 20H4.5C3.67 20 3 19.33 3 18.5V8Z" fill="currentColor" opacity="0.1"/>
    {/* Tab */}
    <path d="M2.5 6.5C2.5 5.67 3.17 5 4 5H9.5L11.5 7.5H2.5V6.5Z" fill="currentColor" opacity="0.2"/>
    {/* Document peek */}
    <rect x="8" y="11" width="8" height="6.5" rx="0.8" stroke="currentColor" strokeWidth="0.9" opacity="0.35"/>
    <line x1="9.5" y1="13" x2="14.5" y2="13" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" opacity="0.25"/>
    <line x1="9.5" y1="14.8" x2="13.5" y2="14.8" stroke="currentColor" strokeWidth="0.7" strokeLinecap="round" opacity="0.2"/>
  </svg>
);

export const CalendarCheckIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Calendar body */}
    <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
    {/* Header bar */}
    <path d="M3 9.5H21" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3.5" y="5.5" width="17" height="4" rx="2" fill="currentColor" opacity="0.1"/>
    {/* Hanging tabs */}
    <line x1="8" y1="3" x2="8" y2="6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="16" y1="3" x2="16" y2="6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Date dots */}
    <circle cx="7.5" cy="12.5" r="0.7" fill="currentColor" opacity="0.3"/>
    <circle cx="12" cy="12.5" r="0.7" fill="currentColor" opacity="0.3"/>
    <circle cx="16.5" cy="12.5" r="0.7" fill="currentColor" opacity="0.3"/>
    <circle cx="7.5" cy="16" r="0.7" fill="currentColor" opacity="0.3"/>
    {/* Checkmark */}
    <path d="M11 16L13 18L17.5 13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
  </svg>
);

export const LearnBookIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Book cover */}
    <path d="M4 4.5C4 3.67 4.67 3 5.5 3H18.5C19.33 3 20 3.67 20 4.5V19.5C20 20.33 19.33 21 18.5 21H5.5C4.67 21 4 20.33 4 19.5V4.5Z" stroke="currentColor" strokeWidth="1.5"/>
    {/* Spine */}
    <line x1="7.5" y1="3" x2="7.5" y2="21" stroke="currentColor" strokeWidth="1.5" opacity="0.4"/>
    <rect x="4" y="3" width="3.5" height="18" rx="0" fill="currentColor" opacity="0.08"/>
    {/* Page lines */}
    <line x1="10" y1="7" x2="17" y2="7" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.35"/>
    <line x1="10" y1="9.5" x2="17" y2="9.5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.25"/>
    <line x1="10" y1="12" x2="15.5" y2="12" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" opacity="0.2"/>
    {/* Bookmark ribbon */}
    <path d="M15 3V8.5L16.5 7L18 8.5V3" fill="currentColor" opacity="0.35"/>
  </svg>
);

export const DiamondIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Diamond top facets */}
    <path d="M12 2L4 9H20L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M4 9L12 22L20 9H4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Inner facet lines */}
    <line x1="8.5" y1="9" x2="12" y2="2" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <line x1="15.5" y1="9" x2="12" y2="2" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <line x1="8.5" y1="9" x2="12" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.25"/>
    <line x1="15.5" y1="9" x2="12" y2="22" stroke="currentColor" strokeWidth="1" opacity="0.25"/>
    {/* Top fill */}
    <path d="M12 2.5L8.5 9H15.5L12 2.5Z" fill="currentColor" opacity="0.15"/>
    {/* Left facet fill */}
    <path d="M4.5 9H8.5L12 21.5L4.5 9Z" fill="currentColor" opacity="0.08"/>
    {/* Sparkle accent */}
    <circle cx="12" cy="9" r="0.6" fill="currentColor" opacity="0.5"/>
  </svg>
);

// ─── REVIEW CARD ICONS ───

export const AlertIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Shield / warning shape */}
    <path d="M12 2L3 7V12C3 16.97 6.84 21.56 12 22.5C17.16 21.56 21 16.97 21 12V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 3L4 7.5V12C4 16.5 7.3 20.6 12 21.5C16.7 20.6 20 16.5 20 12V7.5L12 3Z" fill="currentColor" opacity="0.08"/>
    {/* Exclamation */}
    <line x1="12" y1="8" x2="12" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
    <circle cx="12" cy="17" r="1" fill="currentColor" opacity="0.8"/>
  </svg>
);

export const CheckCircleIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="7.5" fill="currentColor" opacity="0.08"/>
    {/* Decorative inner ring */}
    <circle cx="12" cy="12" r="6" stroke="currentColor" strokeWidth="0.6" opacity="0.2" strokeDasharray="2 2"/>
    {/* Checkmark */}
    <path d="M8 12.5L10.5 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const RepeatIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Top arc with arrow */}
    <path d="M17 4.5C19.5 6.3 21 9 21 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 19.5C4.5 17.7 3 15 3 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Full arcs */}
    <path d="M21 12C21 16.97 16.97 21 12 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    <path d="M3 12C3 7.03 7.03 3 12 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    {/* Arrows */}
    <path d="M14 3L17 4.5L14.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 21L7 19.5L9.5 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Center dot */}
    <circle cx="12" cy="12" r="1.5" fill="currentColor" opacity="0.3"/>
  </svg>
);

// ─── LEARN HUB ICONS ───

export const BrainIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C12 2 8 2 6.5 5C5 8 5.5 10 6 11.5C5 12 4 13.5 4 15.5C4 18 6 20 8.5 20C9.5 20 10.5 19.5 11 19V22H13V19C13.5 19.5 14.5 20 15.5 20C18 20 20 18 20 15.5C20 13.5 19 12 18 11.5C18.5 10 19 8 17.5 5C16 2 12 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 2C12 2 9 3 8 6C7 9 8 11 8 11" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.35"/>
    <path d="M12 2C12 2 15 3 16 6C17 9 16 11 16 11" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.35"/>
    <path d="M8 14C8 14 10 13 12 14C14 15 16 14 16 14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    <line x1="12" y1="6" x2="12" y2="19" stroke="currentColor" strokeWidth="0.8" opacity="0.2"/>
  </svg>
);

export const FileTextIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 2V7C14 7.55 14.45 8 15 8H20" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5 3H14L20 9V20C20 20.83 19.33 21.5 18.5 21.5H5.5C4.67 21.5 4 20.83 4 20V4C4 3 4.5 2.5 5 3Z" fill="currentColor" opacity="0.06"/>
    <line x1="8" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="8" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    <line x1="8" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
  </svg>
);

export const LightbulbIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M9 21H15M10 18H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 2C8.13 2 5 5.13 5 9C5 11.38 6.19 13.47 8 14.74V17C8 17.55 8.45 18 9 18H15C15.55 18 16 17.55 16 17V14.74C17.81 13.47 19 11.38 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 3C8.69 3 6 5.69 6 9C6 11.22 7.21 13.16 9 14.2V16.5H15V14.2C16.79 13.16 18 11.22 18 9C18 5.69 15.31 3 12 3Z" fill="currentColor" opacity="0.08"/>
    {/* Filament */}
    <path d="M10 12C10 12 11 10 12 10C13 10 14 12 14 12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.4"/>
    <line x1="12" y1="10" x2="12" y2="7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
    {/* Rays */}
    <line x1="12" y1="0" x2="12" y2="1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.3"/>
    <line x1="4.2" y1="4.2" x2="5" y2="5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.2"/>
    <line x1="19.8" y1="4.2" x2="19" y2="5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.2"/>
  </svg>
);

export const ListChecksIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Check 1 */}
    <path d="M3 6L5 8L9 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
    {/* Check 2 */}
    <path d="M3 12L5 14L9 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
    {/* Unchecked 3 */}
    <rect x="3" y="17" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.3"/>
    <line x1="12" y1="19" x2="21" y2="19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
  </svg>
);

export const PenLineIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M17 3L21 7L8 20L3 21L4 16L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M5 16L8 20" stroke="currentColor" strokeWidth="1" opacity="0.3"/>
    <path d="M15 5L19 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    <path d="M4.5 16.5L7.5 19.5L3.5 20.5L4.5 16.5Z" fill="currentColor" opacity="0.2"/>
    {/* Writing line */}
    <line x1="3" y1="23" x2="21" y2="23" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.25"/>
  </svg>
);

export const TimerIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="13" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="13" r="6.5" fill="currentColor" opacity="0.06"/>
    {/* Top button */}
    <rect x="10.5" y="1" width="3" height="2.5" rx="0.8" stroke="currentColor" strokeWidth="1.2"/>
    {/* Side button */}
    <path d="M18.5 6.5L20 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Tick marks */}
    {[0, 60, 120, 180, 240, 300].map((angle) => {
      const rad = ((angle - 90) * Math.PI) / 180;
      return (
        <line
          key={angle}
          x1={12 + 7 * Math.cos(rad)}
          y1={13 + 7 * Math.sin(rad)}
          x2={12 + 8 * Math.cos(rad)}
          y2={13 + 8 * Math.sin(rad)}
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          opacity="0.35"
        />
      );
    })}
    {/* Hands */}
    <line x1="12" y1="13" x2="12" y2="8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="12" y1="13" x2="15.5" y2="15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.6"/>
    <circle cx="12" cy="13" r="1" fill="currentColor"/>
  </svg>
);

export const RocketIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C12 2 7 7 7 14L10 17H14L17 14C17 7 12 2 12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 3C12 3 8 7.5 8 13.5L10.5 16.5H13.5L16 13.5C16 7.5 12 3 12 3Z" fill="currentColor" opacity="0.08"/>
    {/* Window */}
    <circle cx="12" cy="10" r="2" stroke="currentColor" strokeWidth="1.2"/>
    <circle cx="12" cy="10" r="0.8" fill="currentColor" opacity="0.3"/>
    {/* Fins */}
    <path d="M7 14C7 14 4 13 3.5 16L7 15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
    <path d="M17 14C17 14 20 13 20.5 16L17 15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
    {/* Exhaust */}
    <path d="M10 17L11 20L12 18.5L13 20L14 17" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
  </svg>
);

export const GraduationCapIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Cap top */}
    <path d="M12 3L1 9L12 15L23 9L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 4L3 9L12 14L21 9L12 4Z" fill="currentColor" opacity="0.1"/>
    {/* Body */}
    <path d="M5 11V17C5 17 8 20 12 20C16 20 19 17 19 17V11" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Tassel pole */}
    <line x1="23" y1="9" x2="23" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
    {/* Tassel */}
    <path d="M22 16L23 16L24 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.5"/>
    <line x1="23" y1="16" x2="23" y2="18.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.35"/>
  </svg>
);

export const PuzzleIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Main piece */}
    <path d="M4 4H10V7C10 7 8.5 7 8.5 8.5C8.5 10 10 10 10 10V14H4V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10 4H14V7C14 7 15.5 7 15.5 8.5C15.5 10 14 10 14 10V14H10" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M4 14H7C7 14 7 12.5 8.5 12.5C10 12.5 10 14 10 14H14V20H4V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M14 14H17C17 14 17 12.5 18.5 12.5C20 12.5 20 14 20 14V20H14V14Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    {/* Fills */}
    <path d="M5 5H9V14H5V5Z" fill="currentColor" opacity="0.06"/>
    <path d="M15 15H19V19H15V15Z" fill="currentColor" opacity="0.08"/>
  </svg>
);

export const VideoIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="5" width="18" height="14" rx="2" fill="currentColor" opacity="0.06"/>
    {/* Play triangle */}
    <path d="M10 8.5V15.5L16 12L10 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10.5 9.5V14.5L15 12L10.5 9.5Z" fill="currentColor" opacity="0.2"/>
    {/* Film strip detail */}
    <line x1="5" y1="4" x2="5" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
    <line x1="8" y1="4" x2="8" y2="6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
    <line x1="5" y1="18" x2="5" y2="20" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
    <line x1="8" y1="18" x2="8" y2="20" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.25"/>
  </svg>
);

export const LayersIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 3L4 7L12 11L20 7L12 3Z" fill="currentColor" opacity="0.1"/>
    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.3"/>
  </svg>
);

// ─── ADDITIONAL FEATURE ICONS ───

export const UploadIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M4 16V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 4V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 8L12 4L16 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 17H19" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.2"/>
    <circle cx="12" cy="4" r="0.8" fill="currentColor" opacity="0.4"/>
  </svg>
);

export const PlayIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.06"/>
    <path d="M10 8.5V15.5L16.5 12L10 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10.5 9.5V14.5L15.5 12L10.5 9.5Z" fill="currentColor" opacity="0.2"/>
  </svg>
);

export const RefreshIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12C4 7.58 7.58 4 12 4C14.52 4 16.77 5.17 18.24 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M20 12C20 16.42 16.42 20 12 20C9.48 20 7.23 18.83 5.76 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M15 7H19V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9 17H5V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="1" fill="currentColor" opacity="0.25"/>
  </svg>
);

export const CrownIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M3 18H21V20H3V18Z" fill="currentColor" opacity="0.15"/>
    <path d="M3 18L5 8L9.5 12L12 5L14.5 12L19 8L21 18H3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M4 17L5.5 9L9.5 12.5L12 6L14.5 12.5L18.5 9L20 17H4Z" fill="currentColor" opacity="0.08"/>
    <circle cx="12" cy="5" r="1" fill="currentColor" opacity="0.5"/>
    <circle cx="5" cy="8" r="0.8" fill="currentColor" opacity="0.4"/>
    <circle cx="19" cy="8" r="0.8" fill="currentColor" opacity="0.4"/>
    <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="20" x2="21" y2="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const ZapIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L4 14H12L11 22L20 10H12L13 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M13 3L5 13.5H12L11 21L19 10.5H12L13 3Z" fill="currentColor" opacity="0.1"/>
    <path d="M12 10H19.5" stroke="currentColor" strokeWidth="0.7" opacity="0.2" strokeLinecap="round"/>
  </svg>
);

export const LockIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="6" y="12" width="12" height="8" rx="1.5" fill="currentColor" opacity="0.06"/>
    <path d="M8 11V8C8 5.79 9.79 4 12 4C14.21 4 16 5.79 16 8V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="15.5" r="1.5" fill="currentColor" opacity="0.5"/>
    <line x1="12" y1="16.5" x2="12" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
  </svg>
);

export const YoutubeIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4.5" width="20" height="15" rx="4" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="5.5" width="18" height="13" rx="3" fill="currentColor" opacity="0.06"/>
    <path d="M10 8.5V15.5L16 12L10 8.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10.5 9.5V14.5L15 12L10.5 9.5Z" fill="currentColor" opacity="0.25"/>
  </svg>
);

export const WarningIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3L2 21H22L12 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 4.5L3.5 20H20.5L12 4.5Z" fill="currentColor" opacity="0.06"/>
    <line x1="12" y1="10" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
    <circle cx="12" cy="18" r="1" fill="currentColor" opacity="0.8"/>
  </svg>
);

export const AlignIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <line x1="3" y1="5" x2="21" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <line x1="3" y1="9" x2="17" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.7"/>
    <line x1="3" y1="13" x2="21" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
    <line x1="3" y1="17" x2="15" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.35"/>
    <line x1="3" y1="21" x2="19" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
  </svg>
);

export const HelpIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.05"/>
    <path d="M9.5 9.5C9.5 8.12 10.62 7 12 7C13.38 7 14.5 8.12 14.5 9.5C14.5 10.88 13.38 12 12 12V13.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
  </svg>
);

export const SparklesIcon = ({ className, size = 24 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Main star */}
    <path d="M12 2L13.5 9L20 8L14 12L17 19L12 14L7 19L10 12L4 8L10.5 9L12 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
    <path d="M12 4L13 9L18 8.5L13.5 11.5L16 17.5L12 13.5L8 17.5L10.5 11.5L6 8.5L11 9L12 4Z" fill="currentColor" opacity="0.12"/>
    {/* Small sparkles */}
    <circle cx="19.5" cy="3.5" r="0.7" fill="currentColor" opacity="0.45"/>
    <circle cx="4" cy="4" r="0.5" fill="currentColor" opacity="0.3"/>
    <circle cx="20" cy="18" r="0.6" fill="currentColor" opacity="0.35"/>
  </svg>
);
